import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { CONFIGURATION_CARD_CLASS, SETTING_GROUP_BAND_CLASS } from '../configurationCard';
import { EmployeePickersCard } from './EmployeePickersCard';
import { REQUIRED_FIELD_CARDS } from './requiredFieldLayout';
import { RequiredFieldsCard } from './RequiredFieldsCard';
import { SettingRow } from './SettingRow';
import type { ShownSection } from './settingVisibility';

/** The `id` a section is anchored at, `#settings-<anchor>`. */
export const sectionAnchor = (anchor: string) => `settings-${anchor}`;

interface Props {
    section: ShownSection;
    readOnly: boolean;
}

/**
 * A section of settings: its heading and count, one card of group bands and their rows, then its Required fields
 * or employee pickers card, which the count leaves out. A section of a card alone has no count.
 */
export function SettingsSection({ section, readOnly }: Props) {
    const id = sectionAnchor(section.layout.anchor);
    const requiredFieldForm = REQUIRED_FIELD_CARDS[section.layout.anchor];

    return (
        <section id={id} aria-labelledby={`${id}-title`} className="flex scroll-mt-20 flex-col gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
                <h3 id={`${id}-title`} className="text-fg m-0 text-2xl leading-8 font-semibold">
                    <FormattedMessage id={`back-office-settings.sections.${section.layout.anchor}`} />
                </h3>
                {section.total > 0 ? (
                    <span className="text-grey-600 text-[13px]">
                        <FormattedMessage
                            id="back-office-settings.count"
                            values={{ settings: section.total, on: section.on, b: (chunks) => chunks }}
                        />
                    </span>
                ) : null}
            </div>
            {section.groups.length > 0 ? (
                <div className={cn(CONFIGURATION_CARD_CLASS, 'new:rounded-[10px]')}>
                    {section.groups.map((group, index) => (
                        <div key={group.id}>
                            <div
                                className={cn(
                                    SETTING_GROUP_BAND_CLASS,
                                    index > 0 && 'border-divider new:border-divider-soft border-t'
                                )}
                            >
                                <FormattedMessage id={`back-office-settings.groups.${group.id}`} />
                            </div>
                            {group.rows.map((row) => (
                                <SettingRow
                                    key={row.setting.key}
                                    setting={row.setting}
                                    layout={row.layout}
                                    readOnly={readOnly}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            ) : null}
            {requiredFieldForm && section.requiredFields.length > 0 ? (
                <RequiredFieldsCard form={requiredFieldForm} entries={section.requiredFields} readOnly={readOnly} />
            ) : null}
            {section.employeePickers.length > 0 ? (
                <EmployeePickersCard entries={section.employeePickers} readOnly={readOnly} />
            ) : null}
        </section>
    );
}
